import { useState, useEffect, useContext } from "react"
import _, { set } from "lodash"
import { GET_BLOCKS_SAVED } from "../queries.js"
import { useLazyQuery } from "@apollo/client"
import FieldValuePerType from "../../FormViewMode/FieldValuePerType.js"
import { LoaderMedium } from "../../../components/Loaders"
import { FirebaseContext } from "../../../firebase"
import { UserContext } from "../../../context/userContext"

export default function StaffDisplayData(props) {
  const [formValues, setFormValues] = useState({})
  const [blocks, setBlocks] = useState([])
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [dateModified, setDateModified] = useState("")
  const [loading, setLoading] = useState(true)
  const [telerecoveryEnabled, setTelerecoveryEnabled] = useState(false)
  const firebase = useContext(FirebaseContext)
  const userCtx = useContext(UserContext)

  useEffect(() => {
    const microservicesRef = firebase.db
      .collection("enterprise-config")
      .doc(userCtx.session.user.enterprise_id)
      .collection("organizations")
      .doc(userCtx.session.user.organization_id)
      .collection("microservices")
    const unsubscribe = microservicesRef.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const microservicesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          setTelerecoveryEnabled(
            microservicesData.find((service) => service.id === "1")?.enable
          )
        } else {
          console.log("No microservices found!")
        }
      },
      (error) => {
        console.log("Error listening to microservices collection:", error)
      }
    )

    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.enterprise_id, userCtx.session.user.organization_id])

  const [getBlocksSaved] = useLazyQuery(GET_BLOCKS_SAVED, {
    onCompleted: (data) => {
      const parsedFormValues = JSON.parse(data?.getBlockSavedByIdStamp?.data)

      const staffMembershipBlock = _.find(parsedFormValues, {
        block_name: "staff__membership",
      })

      if (staffMembershipBlock) {
        const dateModified = _.get(staffMembershipBlock, "date_modified", null)
        if (dateModified) {
          const date = new Date(dateModified._seconds * 1000)
          const formattedDate = `${date.getFullYear()}-${String(
            date.getDate()
          ).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}`
          setDateModified(formattedDate)
          setFormValues((prevValues) => ({
            ...prevValues,
            staff__membership: {
              ...prevValues.staff__membership,
              date_modified: formattedDate,
            },
          }))
        }
      }

      setLoading(false)
    },
    onError: (error) => {
      console.error("Error fetching blocks saved:", error)
    },
  })

  useEffect(() => {
    if (!props?.staffData || !props?.currentStaffIDStamp) return null
    const parsedFormValues = JSON.parse(props?.staffData?.values) || {}
    const filterEmptyValues = _.omitBy(parsedFormValues, _.isEmpty) // Use _.omitBy to filter empty values
    setFormValues(filterEmptyValues)
    setTitle(props?.staffData?.title || "")
    setDescription(props?.staffData?.description || "")
    setBlocks(props?.staffData?.blocks || [])

    getBlocksSaved({
      variables: { idStamp: props?.currentStaffIDStamp },
    })
  }, [
    dateModified,
    getBlocksSaved,
    props,
    props?.staffData?.blocks,
    props?.staffData?.values,
  ])

  const handleFieldsPerBlock = (fields, block_name, block_title) => {
    // Create a new array to hold fields, starting with the dateModified field if block_name matches
    const modifiedFields = [...fields]

    // Check if the block name is 'staff__membership'
    if (block_name === "staff__membership") {
      modifiedFields.unshift({
        field_label: "Last update",
        field_name: "date_modified",
        field_type: "text", // Assuming text type; adjust as necessary
        field_options: null, // Assuming no options needed; adjust as necessary
        field_description: null, // Assuming no description needed; adjust as necessary
        multi_fields: null, // Assuming no multi-fields; adjust as necessary
        date_modified_value: dateModified, // Add the value for the dateModified
      })
    }

    return modifiedFields.map(
      ({
        field_label,
        field_name,
        field_type,
        field_options,
        field_description,
        multi_fields,
        date_modified_value, // Add this line to destructure the new field
      }) => {
        const skipField =
          (field_type === "hidden" ||
            field_type === "sys" ||
            field_name === "staff_account_password") &&
          ![
            "form__location_ids",
            "form_date",
            "form_channel",
            "form_start_time",
            "form_end_time",
            "demo_ssn",
            "activity__program_identity_ids",
            "session__location_ids",
            "session_date",
            "session_channel",
            "session_start_time",
            "session_length",
            "manage_services_type",
            "manage_staff_id_selector",
            "manage_program_identity_ids",
            "manage_location_ids",
            "staff_primary_supervisor",
            "staff_role",
            telerecoveryEnabled && "staff_phone_telerecovery",
          ].includes(field_name)

        if (skipField) return null // Skip fields that are not relevant

        const handleLabel = () => {
          switch (field_name) {
            case "staff_primary_supervisor":
              return "Primary Supervisor"

            case "staff_role":
              return "Staff role"

            case "staff_email_address":
              return "Email"

            case "staff_credentials":
              return "Credentials"

            case "date_modified": // Check for the new field
              return "Last update"

            default:
              return field_label
          }
        }

        return (
          <div key={field_name} className="view-data-field">
            <div className="field-name">
              <strong>{handleLabel()}</strong>
            </div>
            <div className="field-value">
              <FieldValuePerType
                fieldValuePerTypeArg={{
                  blockValues: formValues[block_name],
                  fieldName: field_name,
                  fieldType: field_type,
                  fieldOptions: field_options,
                  description: field_description,
                  multi_fields,
                  value: date_modified_value || dateModified, // Use the new field's value if it exists
                }}
                block_title={block_title}
              />
            </div>
          </div>
        )
      }
    )
  }

  const handleBlocks = () => {
    if (blocks.length === 0) return null // Check for an empty array instead of null

    return _.orderBy(blocks, ["index"]).map(
      ({ block_title, block_name, fields }) => {
        if (!formValues[block_name]) return null

        return (
          <div key={block_name}>
            <hr />
            <h2>{block_title}</h2>
            {handleFieldsPerBlock(fields, block_name, block_title)}
          </div>
        )
      }
    )
  }

  if (loading || props.loadingStaffData) return <LoaderMedium />
  return (
    <div className="staff-display-data">
      <h1>{title}</h1>
      <p>{description}</p>

      {handleBlocks()}
    </div>
  )
}
